import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { ArrowLeft, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { useOrgPath } from '@/lib/pathFor'

interface ContactForm {
  [key: string]: string | undefined
  id?: string
  name: string
  email: string
  phone: string
}

interface ClientFormProps {
  client?: { id: string; name: string; address: string | null; website: string | null; status: string; credit_limit?: number | null }
  contacts?: ContactForm[]
  isEdit?: boolean
}

export default function ClientForm({ client, contacts: initialContacts, isEdit }: ClientFormProps) {
  const pathFor = useOrgPath()
  const [name, setName] = useState(client?.name || '')
  const [address, setAddress] = useState(client?.address || '')
  const [website, setWebsite] = useState(client?.website || '')
  const [status, setStatus] = useState(client?.status || 'active')
  const [creditLimit, setCreditLimit] = useState(client?.credit_limit != null ? String(client.credit_limit) : '')
  const [contacts, setContacts] = useState<ContactForm[]>(
    initialContacts && initialContacts.length > 0
      ? initialContacts.map((c) => ({
          id: c.id,
          name: c.name || '',
          email: c.email || '',
          phone: c.phone || '',
        }))
      : [{ name: '', email: '', phone: '' }]
  )
  const [saving, setSaving] = useState(false)

  function addContact() {
    setContacts([...contacts, { name: '', email: '', phone: '' }])
  }

  function removeContact(index: number) {
    setContacts(contacts.filter((_, i) => i !== index))
  }

  function updateContact(index: number, field: keyof ContactForm, value: string) {
    const updated = [...contacts]
    updated[index] = { ...updated[index], [field]: value }
    setContacts(updated)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    const url = isEdit ? pathFor(`/clients/${client!.id}`) : pathFor('/clients')
    const method = isEdit ? 'patch' : 'post'
    router[method](
      url,
      {
        client: { name, address, website, status, credit_limit: creditLimit },
        contacts,
      },
      {
        onError: () => {
          toast.error('Failed to save client')
          setSaving(false)
        },
      }
    )
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-3">
          <Link href={isEdit ? pathFor(`/clients/${client!.id}`) : pathFor('/clients')}>
            <ArrowLeft className="h-5 w-5 text-muted-foreground cursor-pointer" />
          </Link>
          <h1 className="text-sm font-semibold">{isEdit ? 'Edit Client' : 'New Client'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link href={isEdit ? pathFor(`/clients/${client!.id}`) : pathFor('/clients')}>
            <Button variant="outline" size="sm" className="text-xs">
              Cancel
            </Button>
          </Link>
          <Button
            size="sm"
            className="text-xs"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Client'}
          </Button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="grid grid-cols-2 gap-8">
          {/* Company Details */}
          <div className="space-y-5">
            <h2 className="text-sm font-semibold">Company Details</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Company Name *</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Company name"
                  className="h-10 text-[13px]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Address</label>
                <Input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street address, city, country"
                  className="h-10 text-[13px]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Website</label>
                <Input
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://..."
                  className="h-10 text-[13px]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Credit limit (USD)</label>
                <Input
                  type="number"
                  min={0}
                  step="1"
                  value={creditLimit}
                  onChange={(e) => setCreditLimit(e.target.value)}
                  placeholder="e.g. 25000"
                  className="h-10 w-40 text-[13px]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="h-10 w-40 rounded-md border px-3 text-[13px] cursor-pointer"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">Contacts</h2>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs cursor-pointer"
                onClick={addContact}
              >
                <Plus className="h-3 w-3 mr-1" /> Add Contact
              </Button>
            </div>
            {contacts.map((contact, i) => (
              <div key={i} className="rounded-lg border p-4 space-y-2">
                <div className="flex justify-end">
                  <button
                    onClick={() => removeContact(i)}
                    className="text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-[50px_1fr] gap-y-2 items-center">
                  <span className="text-[11px] text-muted-foreground">Name</span>
                  <Input
                    value={contact.name}
                    onChange={(e) => updateContact(i, 'name', e.target.value)}
                    placeholder="Contact name"
                    className="h-8 text-xs"
                  />
                  <span className="text-[11px] text-muted-foreground">Email</span>
                  <Input
                    value={contact.email}
                    onChange={(e) => updateContact(i, 'email', e.target.value)}
                    placeholder="email@company.com"
                    className="h-8 text-xs"
                  />
                  <span className="text-[11px] text-muted-foreground">Phone</span>
                  <Input
                    value={contact.phone}
                    onChange={(e) => updateContact(i, 'phone', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
